import { memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';

import { RootState } from '@/interfaces/RootState';
import { closeNotification } from '@/features/sliceNotification';
import { cn } from '@/lib/utils';

const severityConfig = {
  success: {
    icon: CheckCircle,
    className: 'bg-green-600 text-white',
  },
  error: {
    icon: AlertCircle,
    className: 'bg-red-600 text-white',
  },
  warning: {
    icon: AlertTriangle,
    className: 'bg-yellow-500 text-white',
  },
  info: {
    icon: Info,
    className: 'bg-blue-600 text-white',
  },
};

const Notification = () => {
  const [isVisible, setIsVisible] = useState(true);
  const dispatch = useDispatch();
  const { text, type, show } = useSelector((rootState: RootState) => ({
    type: rootState.notification.type,
    text: rootState.notification.text,
    show: rootState.notification.show
  }));

  const handleClose = () => {
    setIsVisible(false);
    dispatch(closeNotification());
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (show) {
        dispatch(closeNotification());
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [show, dispatch]);

  useEffect(() => {
    setIsVisible(true);
  }, [show]);

  if (!isVisible) return null;

  const config = severityConfig[type] || severityConfig.info;
  const Icon = config.icon;

  return createPortal(
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[1400]">
      <div
        className={cn(
          'flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg min-w-[300px] max-w-[500px]',
          config.className
        )}
        role="alert"
      >
        <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
        <p className="whitespace-pre-wrap flex-1 text-sm font-medium">{text}</p>
        <button
          onClick={handleClose}
          className="flex-shrink-0 hover:opacity-80 transition-opacity"
          aria-label="Close notification"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>,
    document.body
  );
};

export default memo(Notification);
